import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowUpRight, CircleCheck, ListOrdered } from 'lucide-react';
import { Skeleton } from '@/components/atoms/skeleton';
import { Button } from '@/components/atoms/button';
import { useReviewSettings } from '@/hooks/review-platforms/useReviewSettings';
import { useSetAutoPostPaused } from '@/hooks/review-platforms/useAutoPostSettings';
import {
  PLATFORM_CAPABILITIES,
  visiblePlatformsFor,
  type PlatformConfigRecord,
  type ReviewPlatform,
} from '@/types/review-platform';
import { BusinessLocationPicker } from './BusinessLocationPicker';
import { PlatformConnectCard } from './PlatformConnectCard';
import { PlatformPriorityList } from './PlatformPriorityList';

interface ReviewPlatformsPanelProps {
  locationId: string | null;
  canEdit?: boolean;
  /** OAuth callback signal from `?status=...&provider=...&oauthTokenId=...`. */
  oauthCallback?: {
    status?: 'select-business' | 'error';
    provider?: 'google' | 'facebook';
    oauthTokenId?: string;
    reason?: string;
  };
  /** Called to clear `?status=&provider=&oauthTokenId=&reason=` from the URL. */
  onClearOAuthParams?: () => void;
}

/**
 * The Platforms tab on Settings > Reviews. Owns the OAuth callback handling:
 * when the URL contains `?status=select-business&oauthTokenId=&provider=`, the
 * BusinessLocationPicker is opened to finalize the OAuth flow.
 *
 * Three full-width bands, in the order a contractor actually needs them:
 * summary, then the platforms, then where customers get sent. The previous
 * layout put two summary cards in a three-column grid and the priority list in
 * a 0.38fr rail, so a tenant with nothing connected — the state everyone starts
 * in — met an empty third column and a near-empty rail beside a tall list. The
 * page read as broken rather than as new. Nothing here is allowed to size
 * itself against a column that may have no content.
 */
export function ReviewPlatformsPanel({
  locationId,
  canEdit = true,
  oauthCallback,
  onClearOAuthParams,
}: ReviewPlatformsPanelProps) {
  const { data, isLoading } = useReviewSettings(locationId);

  const platforms = useMemo(() => data?.platforms ?? [], [data?.platforms]);

  // Offered plus retained — a platform we no longer offer but this tenant
  // still has must keep its card, or there is nowhere to disconnect it from.
  const visiblePlatforms = useMemo(() => visiblePlatformsFor(platforms), [platforms]);
  const autoPosting = useMemo(
    () => platforms.filter((config) => config.autoPostEnabled),
    [platforms],
  );
  const pausedAt = data?.settings?.autoPostPausedAt ?? null;
  const setPaused = useSetAutoPostPaused(locationId);
  const platformByType = useMemo(() => {
    const map = new Map<ReviewPlatform, PlatformConfigRecord>();
    for (const p of platforms) map.set(p.platform, p);
    return map;
  }, [platforms]);
  const connectedPlatforms = useMemo(
    () => platforms.filter((platform) => platform.status === 'CONNECTED'),
    [platforms],
  );
  const visibleDestinations = useMemo(
    () =>
      connectedPlatforms
        .slice()
        .sort((a, b) => a.priority - b.priority)
        .filter((platform) => !!platformByType.get(platform.platform)),
    [connectedPlatforms, platformByType],
  );
  const needsAttention = platforms.filter(
    (platform) => platform.status === 'ERROR' || platform.status === 'NEEDS_REAUTH',
  );
  const firstDestination = visibleDestinations[0];
  const syncSources = connectedPlatforms.filter(
    (platform) => PLATFORM_CAPABILITIES[platform.platform].read,
  );

  // ── OAuth callback handling ────────────────────────────────────────────────
  // The backend `/oauth/:platform/callback` redirects here with these params:
  //   ?provider=google|facebook&status=select-business&oauthTokenId=<uuid>
  // (or status=error&reason=...).
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => {
    if (
      oauthCallback?.status === 'select-business' &&
      oauthCallback.oauthTokenId &&
      oauthCallback.provider
    ) {
      setPickerOpen(true);
    }
  }, [oauthCallback?.status, oauthCallback?.oauthTokenId, oauthCallback?.provider]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-5">
        <Skeleton className="h-40 w-full rounded-2xl" />
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <Skeleton className="h-[36rem] w-full rounded-2xl" />
          <Skeleton className="h-72 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-8">
      {oauthCallback?.status === 'error' && (
        <div className="flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            Connection failed
            {oauthCallback.reason ? `: ${decodeURIComponent(oauthCallback.reason)}` : ''}. Please
            try again.
          </span>
        </div>
      )}

      <section className="flex flex-col gap-4 border-b border-border/70 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Review destinations</p>
          <h2 className="mt-1.5 text-xl font-semibold tracking-[-0.025em]">Public review platforms</h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">Connect the profiles where customers can share their experience.</p>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span><strong className="font-semibold tabular-nums text-foreground">{connectedPlatforms.length}</strong> connected</span>
          <span><strong className="font-semibold tabular-nums text-foreground">{syncSources.length}</strong> syncing</span>
          <span className={needsAttention.length ? 'text-amber-700' : ''}><strong className="font-semibold tabular-nums">{needsAttention.length}</strong> need attention</span>
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem] xl:items-start">
        <section className="overflow-hidden rounded-2xl border bg-card shadow-[0_12px_32px_-28px_rgba(57,47,38,0.5)]">
          <div className="flex flex-col gap-3 border-b px-5 py-5 sm:flex-row sm:items-end sm:justify-between sm:px-6">
            <div><h2 className="text-base font-semibold tracking-[-0.015em]">Platforms</h2><p className="mt-1 text-sm leading-5 text-muted-foreground">Connect sources securely, then decide how Notifi handles replies.</p></div>
            <div className="flex items-center gap-3"><span className="text-xs font-medium tabular-nums text-muted-foreground">{connectedPlatforms.length} / {visiblePlatforms.length} active</span>
            {/*
              A bar above the cards said "Posting AI replies automatically to
              Google Business Profile" — which each card already states in its
              Replies row, so it was a second copy that could disagree. Only
              the action survives, and only while there is something to stop:
              offering to halt what is not happening is how a control stops
              meaning anything.
            */}
            {/*
              `canEdit` gates it the same way the per-platform controls are
              gated. Pausing needs `reviews:connect`; a `reviews:view` user
              could otherwise click a live-looking kill switch on a
              high-consequence action and learn it was never theirs from a 403.
            */}
            {canEdit && (autoPosting.length > 0 || pausedAt) ? (
              <Button
                size="sm"
                variant={pausedAt ? 'default' : 'outline'}
                disabled={setPaused.isPending}
                onClick={() => setPaused.mutate({ value: !pausedAt })}
              >
                {pausedAt ? 'Resume auto-post' : 'Pause auto-post'}
              </Button>
            ) : null}
            </div></div>

        {pausedAt ? (
          <div className="flex items-center gap-2.5 rounded-xl border border-amber-500/40 bg-amber-50 px-4 py-2.5 text-[13px] leading-5 text-amber-900 dark:bg-amber-950/20 dark:text-amber-200">
            <span className="size-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden />
            <p className="min-w-0 flex-1">
              <span className="font-medium">Auto-post is paused.</span> Nothing goes out until you
              resume, whatever each platform is set to.
            </p>
          </div>
        ) : null}

        <div className="divide-y">
          {visiblePlatforms.map((platform) => (
            <PlatformConnectCard
              key={platform}
              platform={platform}
              config={platformByType.get(platform) ?? null}
              locationId={locationId}
              canEdit={canEdit}
            />
          ))}
        </div>
        </section>

      {/* Routing is meaningless with nothing connected, so it says so in one
          line instead of rendering an empty card with a hint inside it. */}
      <aside className="space-y-5"><section className="overflow-hidden rounded-2xl border bg-card shadow-[0_12px_32px_-28px_rgba(57,47,38,0.5)]">
        <div className="flex items-start gap-3 border-b border-border/60 px-5 py-5">
          <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
            <ListOrdered className="size-4 text-muted-foreground" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold leading-5">Customer routing</h2>
            <p className="mt-0.5 text-[13px] leading-5 text-muted-foreground">
              Happy customers see the first connected destination after a positive rating.
              {locationId ? ' Scoped to this location.' : ' Tenant-wide.'}
            </p>
          </div>
        </div>
        <div className="px-5 py-5">
          {visibleDestinations.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">
              Connect a platform above and it will appear here as a destination.
            </p>
          ) : (
            <>
              <PlatformPriorityList
                platforms={platforms}
                locationId={locationId}
                disabled={!canEdit}
              />
              {visibleDestinations.length === 1 ? (
                <p className="mt-3 text-xs text-muted-foreground">
                  Connect a second platform to enable drag-to-reorder.
                </p>
              ) : null}
            </>
          )}
        </div>
        {firstDestination ? <p className="flex items-center gap-1.5 border-t px-5 py-4 text-xs text-muted-foreground"><ArrowUpRight className="size-3.5" />First stop: {PLATFORM_CAPABILITIES[firstDestination.platform].label}</p> : null}
      </section>
      <p className="flex items-center gap-2 px-1 text-xs text-muted-foreground"><CircleCheck className="size-3.5 text-[#4a7860]" /> {locationId ? 'Applies to this location' : 'Applies across your workspace'}</p>
      </aside></div>

      {/* OAuth final-step picker */}
      {pickerOpen && oauthCallback?.oauthTokenId && oauthCallback.provider && (
        <BusinessLocationPicker
          open
          platformSlug={oauthCallback.provider}
          oauthTokenId={oauthCallback.oauthTokenId}
          locationId={locationId}
          onClose={() => {
            setPickerOpen(false);
            onClearOAuthParams?.();
          }}
          onSuccess={() => {
            // settings query is invalidated by useSelectBusinessLocation
          }}
        />
      )}
    </div>
  );
}
